import { Component, inject, Inject, Input, OnInit } from '@angular/core';
import { DynamicFormService } from '@ntap/common';
import {
  AttributeStateService,
  BackendDataSourceService,
  BusinessItem,
  commonActionSuccessMessageMapping,
  CoreConfig,
  CoreConfigService,
  InputComponentUtil,
  InputControlData,
  InputUtils,
  Item,
  ItemView,
  ItemViewProviderDirective,
  ListView,
  ListViewProviderDirective,
  NotificationService,
  NtapGlobalConstants,
  Util,
  View
} from '@ntap/core';
import { PrimeNGTableComponent, TableData } from '@ntap/primeng';
import { FormControl, FormGroup } from '@angular/forms';
import { map, of, switchMap, tap } from 'rxjs';
import { TableMetaWithDataKey } from '../editable-table.component/editable-table.component';
import { CustomItemViewProviderDirective } from '@app/directives/row-item-view-provider.directive';
import { ObrazacUnosaService } from '../fast-data-entry/service/obrazac-unosa.service';
import { FAST_OBRAZAC_TOKEN } from '../fast-data-entry-container/fast-data-entry-container.component';
import { ARHIV_POSJEDNIK } from '@app/shared/models/types';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-inplace-table-row]',
  templateUrl: './inplace-table-row.component.html',
  providers: [
    DynamicFormService,
    // { provide: FORM_SERVICE_PROVIDER_TOKEN, useExisting: DynamicFormService },
    {
      provide: ItemViewProviderDirective,
      useClass: CustomItemViewProviderDirective
    },
    {
      provide: CustomItemViewProviderDirective,
      useExisting: ItemViewProviderDirective
    }
  ]
})
export class InplaceTableRowComponent
  extends PrimeNGTableComponent
  implements OnInit
{
  @Input() public parentItem: any;
  @Input() public relToParentItemKey = 'parent_id';
  public formInvalid = true;
  @Input() item: Item;
  @Input() tableMetaWithDataKey: TableMetaWithDataKey;
  @Input() tableData: TableData;
  @Input() tableItem: Item;
  @Input() editing: boolean;
  @Input() rowIndex: number;
  @Input() readOnly = false;
  @Input() readOnly$ = of(false);
  public inputs: InputControlData[];
  clonedItems: { [s: string]: Item } = {};
  private viewName: string;

  private fastDataEntryService = inject(ObrazacUnosaService);
  private notificationService = inject(NotificationService);

  constructor(
    protected override listViewProvider: ListViewProviderDirective<ListView>,
    @Inject(CoreConfigService) protected override coreConfig: CoreConfig,
    protected override attributeStateService: AttributeStateService,
    protected backendDataSource: BackendDataSourceService,
    protected dynamicFormService: DynamicFormService,
    protected itemViewProvider: CustomItemViewProviderDirective<ItemView>,
    @Inject(FAST_OBRAZAC_TOKEN)
    private mojObrazacItemView: ItemViewProviderDirective<ItemView>
  ) {
    super(listViewProvider, coreConfig, attributeStateService);
  }

  readonly OSTALI_NAZIVI_MASTER_ID = 'sv~naziv~entitet_id';
  readonly AGENT_ID = 'en~agent~id';

  ngOnInit() {
    this.addSubscription(
      this.fastDataEntryService.selectEntryTemplate$
        .pipe(
          tap((e) => {
            this.viewName = e.name;
          })
        )
        .subscribe()
    );
    this.mojObrazacItemView.view$
      .pipe(
        tap((view) => {
          if (view) {
            this.inputs = this.extractInputs(view, this.item);
            this.inputs =
              this.fastDataEntryService.getItemViewAttrsOrderedByListViewAttrs(
                this.tableMetaWithDataKey.view,
                this.inputs
              );
            this.initializeControls(this.inputs);
          }
        })
      )
      .subscribe();

    this.addSubscription(
      this.dynamicFormService.formGroup$
        .pipe(
          tap((fg) => {
            this.addRowRequiredValidators(fg);
          }),
          switchMap((fg) => {
            return fg.statusChanges.pipe(
              map((status) => {
                this.formInvalid = status !== 'VALID';
              })
            );
          })
        )
        .subscribe()
    );
    this.itemViewProvider.setItem(this.item);
  }

  private addRowRequiredValidators(fg: FormGroup) {
    fg.addValidators([
      (value: any) => {
        const anyValuePresent = Object.keys(value.controls)
          .map((controlName) => value.controls[controlName])
          .some((formControl: FormControl) => {
            return (
              (typeof formControl.value === 'string' &&
                formControl.value.length > 0) ||
              (formControl.value !== undefined && formControl.value !== null)
            );
          });
        return anyValuePresent ? null : { noValuesEntered: '' };
      }
    ]);
  }

  public extractInputs(viewMeta: View, item: any) {
    const inputs = InputUtils.attrsToInputControls(
      viewMeta.attributes,
      (inputControl: InputControlData) =>
        item[inputControl.attributeMetadata.name],
      () => item,
      () => this.listViewProvider.viewName
    );
    return inputs;
  }

  initializeControls(inputControlParams: InputControlData[]) {
    this.dynamicFormService.reInitializeFormGroup();
    inputControlParams.forEach((param) => {
      const validators = InputComponentUtil.getValidators(param);
      this.dynamicFormService.registerFormControl(
        param.attributeMetadata.name,
        { value: param.itemValue, disabled: param.disabled },
        validators
      );
    });
  }

  onRowEditInit(tableMeta: TableMetaWithDataKey, item: any) {
    const itemId = item[tableMeta.dataKey];
    this.clonedItems[itemId] = { ...item };
  }

  createBusinessItem(item: any) {
    const itemMainType = item['$mainType'] || item['$itemType'];
    const itemWithMainType = Object.assign(
      { $mainType: itemMainType },
      this.toMainItemData(this.inputs[0].objectData)
    );
    if (!!this.relToParentItemKey && this.parentItem) {
      const relationToParentAttrName = Util.getFullAttrName(
        itemMainType,
        this.relToParentItemKey
      );

      let parentId: string;
      if (
        relationToParentAttrName == this.OSTALI_NAZIVI_MASTER_ID &&
        itemMainType === ARHIV_POSJEDNIK
      ) {
        parentId = this.parentItem[this.AGENT_ID];
      } else {
        parentId =
          this.parentItem[
            Util.getFullAttrName(
              this.parentItem['$mainType'],
              NtapGlobalConstants.ATTR_id
            )
          ];
      }
      itemWithMainType[relationToParentAttrName] = parentId;
    }
    const businessItem = new BusinessItem(itemWithMainType);
    return businessItem;
  }

  onRowEditSave(tableMeta: TableMetaWithDataKey, item: any, rowIndex: number) {
    delete this.clonedItems[item[tableMeta.dataKey]];

    const onlyItemMainTypeAttrs: any = Object.assign(this.toMainItemData(item));
    const businessItem = Object.assign(
      this.createBusinessItem(onlyItemMainTypeAttrs)
    );
    Object.assign(businessItem.data, this.getDirtyFormValues());
    this.backendDataSource
      .saveViewData(this.viewName, businessItem)
      .pipe(
        switchMap((savedItem: any) => {
          const savedItemToRefreshState = Util.toBasicItemData(savedItem);
          return this.backendDataSource
            .getViewData(
              this.listViewProvider.viewName,
              savedItemToRefreshState
            )
            .pipe(
              tap((refreshed: Array<Item>) => {
                this.listViewProvider.updateItem(refreshed[0], rowIndex);
                this.notificationService.success(
                  savedItem.$infoMessage?.msg ||
                    Util.pluralizeMsg(commonActionSuccessMessageMapping)
                );
              })
            );
        })
      )
      .subscribe();
  }

  private getDirtyFormValues(): any {
    const formGroup = this.dynamicFormService.formGroup;
    const dirtyValues: any = {};
    Object.keys(formGroup.controls).forEach((controlName) => {
      const control = formGroup.controls[controlName];
      if (control.dirty) {
        dirtyValues[controlName] = control.value;
      }
    });
    return dirtyValues;
  }

  private toMainItemData(item: any) {
    const itemMainType = item['$mainType'] || item['$itemType'];

    const onlyItemMainTypeAttrs: any = {};

    Object.keys(item)
      .filter((key) => key.startsWith(itemMainType))
      .forEach((key) => {
        onlyItemMainTypeAttrs[key] = item[key];
      });

    onlyItemMainTypeAttrs['$itemType'] = item['$itemType'];
    onlyItemMainTypeAttrs['$mainType'] = item['$mainType'];
    return onlyItemMainTypeAttrs;
  }

  // onRowEditCancel(item: any) {
  //   this.listViewProvider.cancelUpdateItem(item);
  // }

  onRowDelete(tableMeta: TableMetaWithDataKey, item: any, rowIndex: number) {
    const itemId = item[tableMeta.dataKey];
    delete this.clonedItems[itemId];
    const businessItem = new BusinessItem(this.toMainItemData(item));
    const tableRows = this.tableData.rows || 1;
    const itemIndex = tableMeta.pagingSetup.lazy
      ? rowIndex - Math.floor(rowIndex / tableRows) * tableRows
      : rowIndex;
    this.listViewProvider
      .deleteItemAtIndex(businessItem, itemIndex)
      .subscribe();
  }
}
